import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Typography,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
  CONTACT_CATEGORIES,
  CATEGORY_FIELDS,
  COMMON_FIELDS,
  validateEmail,
  validateRequired,
} from '../../constants/contactData';
import { CONTACT_URL } from '../../constants/api';
import { getDevFlag } from '../../constants/devFlags';
import {
  formCardSx,
  formSectionTitleSx,
  fieldContainerSx,
  categorySelectorSx,
  textFieldSx,
  selectFieldSx,
  messageFieldSx,
  submitButtonContainerSx,
  submitButtonSx,
  alertContainerSx,
  categoryDescriptionSx,
  twoColumnGridSx,
} from '../../styles/contact';

const getInitialFormState = (defaultCategory = '') => ({
  category: defaultCategory,
  name: '',
  email: '',
  company: '',
  phone: '',
  message: '',
  // Category-specific fields
  useCase: '',
  teamSize: '',
  feedbackType: '',
  issueType: '',
  subject: '',
});

function ContactForm({ defaultCategory = '', initialValues = {}, onSuccess, inModal = false, mcpOnly = false, inquiryPurpose }) {
  const theme = useTheme();
  const mcpEnabled = getDevFlag('MCP');
  const targeted = mcpOnly || !!inquiryPurpose;
  const purpose = mcpOnly ? 'MCP access' : inquiryPurpose;
  const category = targeted ? 'api-data-access' : defaultCategory || 'enterprise-sales';
  const initialTab = category === 'api-data-access' ? 'systematic' : category === 'enterprise-sales' ? 'enterprise' : 'help';
  const [tab, setTab] = useState(initialTab);
  const [researchAccess, setResearchAccess] = useState('Enterprise Research');
  const [delivery, setDelivery] = useState('Not sure');
  const [formData, setFormData] = useState(() => ({ ...getInitialFormState(category), ...initialValues, ...(targeted ? { category } : {}) }));
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate category
    if (!validateRequired(formData.category)) {
      newErrors.category = 'Please select a category';
    }

    // Validate common required fields
    if (!validateRequired(formData.name)) {
      newErrors.name = 'Name is required';
    }

    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!validateRequired(formData.message)) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    const inquiry = Object.fromEntries(['category', 'name', 'email', 'company', 'phone', 'message',
      ...(tab === 'systematic' ? ['useCase'] : (CATEGORY_FIELDS[formData.category] || []).map(field => field.name))]
      .map(key => [key, formData[key]]));

    try {
      const response = await fetch(CONTACT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(targeted ? {
          category,
          name: formData.name,
          email: formData.email,
          company: formData.company,
          useCase: purpose,
          message: formData.message,
        } : tab === 'enterprise' ? { ...inquiry, message: `${researchAccess} access inquiry\n\n${formData.message}` }
          : tab === 'systematic' ? { ...inquiry, useCase: `${delivery}; ${formData.useCase || ''}` } : inquiry),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData(getInitialFormState(formData.category));
        // Call onSuccess callback after a short delay to show success message
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1500);
        }
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCategory = CONTACT_CATEGORIES.find(
    (cat) => cat.id === formData.category
  );
  const categoryFields = !targeted && formData.category && tab !== 'systematic' ? CATEGORY_FIELDS[formData.category] || [] : [];
  const commonFields = targeted ? COMMON_FIELDS.filter((field) => field.name !== 'phone') : COMMON_FIELDS;

  // In modal mode, use simpler styling since dialog provides the container
  const formSx = inModal
    ? { p: 2 }
    : formCardSx(theme);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={formSx}>
      {!inModal && (
        <Typography variant="h5" color="primary" sx={formSectionTitleSx}>
          Send us a message
        </Typography>
      )}

      {/* Status Alerts */}
      {submitStatus === 'success' && (
        <Alert severity="success" sx={alertContainerSx}>
          {mcpOnly ? "Thank you for your MCP inquiry. We'll get back to you soon." : "Thank you for your message. We'll get back to you soon."}
        </Alert>
      )}
      {submitStatus === 'error' && (
        <Alert severity="error" sx={alertContainerSx}>
          There was an error sending your message. Please try again or email us directly.
        </Alert>
      )}

      {!targeted && <>
        <FormControl component="fieldset" fullWidth sx={{ mb: 3, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '1.1rem', color: 'text.primary' }}>1. What would you like to discuss?</FormLabel>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>Choose an option below, then tell us about your request.</Typography>
          <RadioGroup name="inquiryType" value={tab} onChange={(_, next) => {
            setTab(next); setErrors({}); setSubmitStatus(null);
            setFormData(previous => ({ ...previous, category: next === 'systematic' ? 'api-data-access' : next === 'enterprise' ? 'enterprise-sales' : 'support' }));
          }}>
            {[
              ['enterprise', mcpEnabled ? 'Enterprise & MCP access' : 'Enterprise access', mcpEnabled ? 'Research for your team and MCP for your AI assistant.' : 'Research access and licensing for your team.'],
              ['systematic', 'Systematic data access', mcpEnabled ? 'S3 or Snowflake delivery, with MCP access available too.' : 'Dataset delivery through S3 or Snowflake.'],
              ['help', 'Support & feedback', 'Account help, product feedback, or another question.'],
            ].map(([value, label, description]) => <FormControlLabel key={value} value={value}
              control={<Radio inputProps={{ 'aria-label': label }} disabled={isSubmitting} />}
              label={<Box><Typography fontWeight={600}>{label}</Typography><Typography variant="body2" color="text.secondary">{description}</Typography></Box>}
              sx={{ m: 0, mb: 1, p: 1.5, alignItems: 'flex-start', border: 1, borderRadius: 2,
                borderColor: tab === value ? 'primary.main' : 'divider', bgcolor: tab === value ? 'action.selected' : 'transparent' }} />)}
          </RadioGroup>
        </FormControl>
        <Typography variant="h6">{tab === 'enterprise' ? 'Research access for you and your team' : tab === 'systematic' ? 'Data for your research pipelines' : 'How can we help?'}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>{tab === 'enterprise' ? (mcpEnabled ? 'Use SoftwareIQ Research with your team, or connect your AI assistant through MCP. Contact us to discuss user access and team licensing.' : 'Use SoftwareIQ Research with your team. Contact us to discuss access and team licensing.') : tab === 'systematic' ? (mcpEnabled ? 'Discuss MCP access or license datasets for recurring S3 delivery or a Snowflake pipeline. Tell us about your coverage, history, and delivery needs.' : 'License datasets for S3 delivery or a Snowflake pipeline. Tell us about your coverage, history, and delivery needs.') : 'Get help with your account or share product feedback.'}</Typography>
      </>}
      <Box>
      {!targeted && tab === 'enterprise' && mcpEnabled && <TextField select fullWidth label="Access of interest" value={researchAccess} onChange={event => setResearchAccess(event.target.value)} sx={{ mb: 3 }}>
        {['Enterprise Research', 'MCP', 'Enterprise Research and MCP'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>}
      {!targeted && tab === 'systematic' && <>
        <TextField select fullWidth label="Access or delivery preference" value={delivery} onChange={event => setDelivery(event.target.value)} sx={{ mb: 3 }}>
          {['S3 delivery', 'Snowflake delivery', ...(mcpEnabled ? ['MCP'] : []), 'Not sure'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
        </TextField>
        <TextField fullWidth multiline minRows={2} label="Datasets, coverage and history" name="useCase" value={formData.useCase} onChange={handleChange} sx={{ mb: 3 }} />
      </>}
      {/* Category Selection */}
      {targeted ? <Typography color="text.secondary" sx={categoryDescriptionSx}>
        {mcpOnly ? 'Tell us how you would like to use SoftwareIQ MCP. We’ll follow up about access, datasets, and per-user licensing.' : `Tell us about your ${inquiryPurpose} requirements. We’ll follow up about datasets, coverage, delivery, and terms.`}
      </Typography> : tab === 'help' ? <Box sx={categorySelectorSx}>
        <FormControl fullWidth error={!!errors.category}>
          <InputLabel id="category-label">What can we help you with?</InputLabel>
          <Select
            labelId="category-label"
            id="category"
            name="category"
            value={formData.category}
            label="What can we help you with?"
            onChange={handleChange}
            sx={selectFieldSx(theme)}
          >
            {CONTACT_CATEGORIES.filter(cat => !['api-data-access', 'enterprise-sales'].includes(cat.id)).map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
          {errors.category && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
              {errors.category}
            </Typography>
          )}
        </FormControl>
        {selectedCategory && (
          <Typography color="text.secondary" sx={categoryDescriptionSx}>
            {selectedCategory.description}
          </Typography>
        )}
      </Box> : null}

      {/* Common Fields - Two Column Layout */}
      <Box sx={twoColumnGridSx}>
        {commonFields.map((field) => (
          <Box key={field.name} sx={fieldContainerSx}>
            <TextField
              fullWidth
              id={field.name}
              name={field.name}
              label={field.label}
              type={field.type}
              value={formData[field.name]}
              onChange={handleChange}
              required={field.required}
              error={!!errors[field.name]}
              helperText={errors[field.name]}
              sx={textFieldSx(theme)}
            />
          </Box>
        ))}
      </Box>

      {/* Category-Specific Fields */}
      {categoryFields.length > 0 && (
        <Box sx={twoColumnGridSx}>
          {categoryFields.map((field) => (
            <Box key={field.name} sx={fieldContainerSx}>
              {field.type === 'select' ? (
                <FormControl fullWidth>
                  <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
                  <Select
                    labelId={`${field.name}-label`}
                    id={field.name}
                    name={field.name}
                    value={formData[field.name]}
                    label={field.label}
                    onChange={handleChange}
                    sx={selectFieldSx(theme)}
                  >
                    {field.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  id={field.name}
                  name={field.name}
                  label={field.label}
                  type={field.type}
                  value={formData[field.name]}
                  onChange={handleChange}
                  required={field.required}
                  sx={textFieldSx(theme)}
                />
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Message Field */}
      <Box sx={fieldContainerSx}>
        <TextField
          fullWidth
          id="message"
          name="message"
          label="Message"
          multiline
          rows={5}
          value={formData.message}
          onChange={handleChange}
          required
          error={!!errors.message}
          helperText={errors.message}
          sx={messageFieldSx(theme)}
        />
      </Box>

      {/* Submit Button */}
      <Box sx={submitButtonContainerSx}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isSubmitting}
          endIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
          sx={submitButtonSx}
        >
          {isSubmitting ? 'Sending...' : mcpOnly ? 'Send MCP inquiry' : 'Send Message'}
        </Button>
      </Box>
      </Box>
    </Box>
  );
}

export default ContactForm;
